/**
 * Can this device sell right now, and if not, why?
 *
 * Specification: ../test/outbox.spec.ts. Master plan §3 K4, §3 K8.
 *
 * The single most important sentence in the observability design:
 *
 *     OFFLINE IS NOT AN OUTAGE.
 *
 * A till with no WAN and a pocket full of leased receipt numbers is trading
 * perfectly well. Only `cannot_sell` burns the error budget. Conflating the
 * two means paging all night for restaurants that are fine, and — because
 * alert fatigue is real — missing the ones that are not.
 *
 * Three named states, surfaced to staff in those words, so a server can tell a
 * guest something true rather than watching a spinner.
 */

export type SellState = "online" | "degraded_selling" | "cannot_sell";

export type DegradationReason =
  | "OFFLINE"
  | "NO_RECEIPT_NUMBERS"
  | "ENTITLEMENT_EXPIRED"
  /** Past expiry, inside grace. Still selling — but the venue must be told. */
  | "CONFIG_STALE"
  | "CONFIG_EXPIRED"
  | "VERSION_TOO_OLD"
  | "OUTBOX_DEEP";

export interface DeviceCondition {
  readonly connected: boolean;
  readonly outboxDepth: number;
  /** Receipt numbers left in the device's lease. Zero offline means no sale. */
  readonly numbersRemaining: number;
  /** When the signed entitlement token stops being valid. Null = none issued. */
  readonly entitlementValidUntil: number | null;
  /** Past this, a degraded device stops entirely. Null = no grace configured. */
  readonly configGraceUntil: number | null;
  /**
   * When the cached config lapsed. Null = none cached, or not tracked.
   *
   * Distinct from the grace instant because the window BETWEEN them is a real
   * state a person has to act on: the config server has been unreachable for a
   * while, the till is still trading, and somebody should look before the grace
   * runs out mid-service. Without this field that window is silent, and the
   * first thing anyone learns is a dead till.
   */
  readonly configExpiresAt?: number | null;
  readonly now: number;
  readonly appVersion: number;
  readonly minOperatingVersion: number;
  /** Depth past which the unsynced money is worth surfacing. */
  readonly outboxDepthWarning?: number;
}

export interface SellAssessment {
  readonly state: SellState;
  readonly reasons: readonly DegradationReason[];
  /**
   * Only a genuine inability to sell counts against the SLO.
   *
   * Also alert on `degraded_selling` DURATION (over ~4h) and on outbox depth
   * separately — money not yet in the books is a business risk even while the
   * venue is happy — but neither is an availability breach.
   */
  readonly burnsErrorBudget: boolean;
}

const DEFAULT_OUTBOX_WARNING = 500;

export function sellState(condition: DeviceCondition): SellAssessment {
  const blocking: DegradationReason[] = [];
  const degraded: DegradationReason[] = [];

  // An old device may not take NEW orders. It may still drain what it holds —
  // see canDrain, and the comment there about why the two gates differ.
  if (condition.appVersion < condition.minOperatingVersion) blocking.push("VERSION_TOO_OLD");

  // After grace the device refuses NEW transactions — but never loses queued
  // ones. `canDrain` below is deliberately not gated on any of this.
  const pastGrace =
    condition.configGraceUntil !== null && condition.now > condition.configGraceUntil;
  if (pastGrace) blocking.push("CONFIG_EXPIRED");

  // The one that actually stops a restaurant: no numbers left and no server to
  // lease more from. A receipt with no number is not a receipt.
  if (condition.numbersRemaining <= 0 && !condition.connected) {
    blocking.push("NO_RECEIPT_NUMBERS");
  }

  // Reported only while it is still merely stale. Once past grace the device
  // cannot sell at all, and listing both would have staff reading "config is
  // out of date" next to "config has expired" and acting on the milder one.
  if (
    !pastGrace &&
    condition.configExpiresAt !== null &&
    condition.configExpiresAt !== undefined &&
    condition.now > condition.configExpiresAt
  ) {
    degraded.push("CONFIG_STALE");
  }

  if (!condition.connected) degraded.push("OFFLINE");

  if (
    condition.entitlementValidUntil !== null &&
    condition.now > condition.entitlementValidUntil
  ) {
    degraded.push("ENTITLEMENT_EXPIRED");
  }

  if (condition.outboxDepth > (condition.outboxDepthWarning ?? DEFAULT_OUTBOX_WARNING)) {
    degraded.push("OUTBOX_DEEP");
  }

  if (blocking.length > 0) {
    return { state: "cannot_sell", reasons: [...blocking, ...degraded], burnsErrorBudget: true };
  }
  if (degraded.length > 0) {
    return { state: "degraded_selling", reasons: degraded, burnsErrorBudget: false };
  }
  return { state: "online", reasons: [], burnsErrorBudget: false };
}

// --- Version gates ---------------------------------------------------------

export interface VersionGates {
  /** Below this, no new orders. */
  readonly minOperatingVersion: number;
  /** Below this, the server will not accept an upload at all. Keep it LOW. */
  readonly minUploadVersion: number;
}

export const canOperate = (appVersion: number, gates: VersionGates): boolean =>
  appVersion >= gates.minOperatingVersion;

/**
 * May this device still drain its outbox?
 *
 * The two gates exist because collapsing them into one destroys revenue. A
 * tablet from a seasonal venue that has sat in a drawer since March is holding
 * a real evening of takings; refusing its outbox to enforce a release policy
 * trades a day's money for a tidy version number.
 *
 * So `minUploadVersion` sits far below `minOperatingVersion` and moves only
 * when an event schema genuinely cannot be upcast any further.
 */
export const canDrain = (appVersion: number, gates: VersionGates): boolean =>
  appVersion >= gates.minUploadVersion;
